import { GameState, TurnEndGameEvent } from '../types/game';
import { revertScoreDelta } from '../domain/stats';
import { updatePlayerPositions } from './reducerHelpers';
import { makeEmptyDiceMask } from '../domain/gameRules';

type DebugFn = (label: string, payload: unknown) => void;

export function handleUndoLastAction(state: GameState, debug: DebugFn): GameState {
  if (state.history.length === 0) return state;
  debug('UNDO:start', { last: state.history[state.history.length - 1] });

  let updatedPlayers = [...state.players].map(p => ({ ...p, isWinner: false }));
  const newHistory = state.history.slice(0, -1);
  const eventList = state.events || [];
  const lastTurn = Math.max(...eventList.map(e => e.turn), 0);
  const toRevert = eventList.filter(e => e.turn === lastTurn);
  const turnStatsAdjusted = new Set<string>();

  for (let i = toRevert.length - 1; i >= 0; i--) {
    const ev = toRevert[i];
    switch (ev.type) {
      case 'SCORE': {
        const { playerId, delta } = ev.payload;
        const idx = updatedPlayers.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const pl = { ...updatedPlayers[idx] };
          pl.score -= delta;
          const undoTurn = !turnStatsAdjusted.has(pl.id);
          updatedPlayers[idx] = revertScoreDelta(pl, delta, undoTurn);
          if (undoTurn) turnStatsAdjusted.add(pl.id);
        }
        break;
      }
      case 'PENALTY': {
        const { playerId, delta } = ev.payload;
        const idx = updatedPlayers.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const pl = { ...updatedPlayers[idx] };
          pl.score -= delta;
          const undoTurn = !turnStatsAdjusted.has(pl.id);
          updatedPlayers[idx] = revertScoreDelta(pl, delta, undoTurn);
          if (undoTurn) turnStatsAdjusted.add(pl.id);
        }
        break;
      }
      case 'OVERTAKE': {
        const { overtakerId, bonus, penalties } = ev.payload;
        const overIdx = updatedPlayers.findIndex(p => p.id === overtakerId);
        if (overIdx !== -1) {
          const pl = { ...updatedPlayers[overIdx] };
          pl.score -= bonus;
          const undoTurn = !turnStatsAdjusted.has(pl.id);
          let reverted = revertScoreDelta(pl, bonus, undoTurn);
          if (undoTurn) turnStatsAdjusted.add(pl.id);
          reverted = {
            ...reverted,
            statistics: {
              ...reverted.statistics,
              totalOvertakes: Math.max(0, reverted.statistics.totalOvertakes - penalties.length),
            },
          };
          updatedPlayers[overIdx] = reverted;
        }
        penalties.forEach((pp) => {
          const pi = updatedPlayers.findIndex(p => p.id === pp.playerId);
          if (pi !== -1) {
            const pl = { ...updatedPlayers[pi] };
            pl.score -= pp.delta;
            updatedPlayers[pi] = revertScoreDelta(pl, pp.delta, false);
          }
        });
        break;
      }
      case 'ZONE_TRIGGERED': {
        // Odkrycie strefy zostaje – to wiedza publiczna od chwili, gdy inni ją zobaczyli.
        const { playerId, zoneDelta, overtakeBonus, penalties } = ev.payload;
        const total = (zoneDelta || 0) + (overtakeBonus || 0);
        const idx = updatedPlayers.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const pl = { ...updatedPlayers[idx] };
          pl.score -= total;
          const undoTurn = !turnStatsAdjusted.has(pl.id);
          let reverted = revertScoreDelta(pl, total, undoTurn);
          if (undoTurn) turnStatsAdjusted.add(pl.id);
          if (penalties.length > 0) {
            reverted = {
              ...reverted,
              statistics: {
                ...reverted.statistics,
                totalOvertakes: Math.max(0, reverted.statistics.totalOvertakes - penalties.length),
              },
            };
          }
          updatedPlayers[idx] = reverted;
        }
        penalties.forEach((pp) => {
          const pi = updatedPlayers.findIndex(p => p.id === pp.playerId);
          if (pi !== -1) {
            const pl = { ...updatedPlayers[pi] };
            pl.score -= pp.delta;
            updatedPlayers[pi] = revertScoreDelta(pl, pp.delta, false);
          }
        });
        break;
      }
    }
  }

  if (toRevert.length === 0) {
    const lastEntry = state.history[state.history.length - 1];
    updatedPlayers = state.players.map(player => {
      if (player.id === lastEntry.playerId) {
        return {
          ...player,
          score: lastEntry.newScore - lastEntry.points,
          hasFirstEntry: player.hasFirstEntry && lastEntry.points > 0 ? false : player.hasFirstEntry,
          isWinner: false,
        };
      }
      return { ...player, isWinner: false };
    });
  }

  const remainingEvents = eventList.filter(e => e.turn !== lastTurn);
  const currentPlayerIndex = Math.max(0, (state.currentPlayerIndex - 1 + state.players.length) % state.players.length);
  const result = {
    ...state,
    players: updatePlayerPositions(updatedPlayers),
    history: newHistory,
    events: remainingEvents,
    isGameOver: false,
    winner: undefined,
    currentPlayerIndex,
  };
  debug('UNDO:end', {});
  return result;
}

export function handleUndoTurn(state: GameState, turn: number): GameState {
  if (state.mode === 'REAL') return state;

  const eventList = state.events || [];
  const turnEvents = eventList.filter(e => e.turn === turn);
  const turnEnd = turnEvents.find((e): e is TurnEndGameEvent => e.type === 'TURN_END');
  if (!turnEnd) return state;

  let updatedPlayers = [...state.players].map(p => ({ ...p, isWinner: false }));
  const turnStatsAdjusted = new Set<string>();

  for (let i = turnEvents.length - 1; i >= 0; i--) {
    const ev = turnEvents[i];
    switch (ev.type) {
      case 'SCORE': {
        const { playerId, delta } = ev.payload;
        const idx = updatedPlayers.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const pl = { ...updatedPlayers[idx] };
          pl.score -= delta;
          if (!turnStatsAdjusted.has(pl.id)) {
            pl.statistics = { ...pl.statistics, totalTurns: Math.max(0, pl.statistics.totalTurns - 1) };
            turnStatsAdjusted.add(pl.id);
          }
          updatedPlayers[idx] = pl;
        }
        break;
      }
      case 'PENALTY': {
        const { playerId, delta } = ev.payload;
        const idx = updatedPlayers.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const pl = { ...updatedPlayers[idx] };
          pl.score -= delta;
          if (!turnStatsAdjusted.has(pl.id)) {
            pl.statistics = { ...pl.statistics, totalTurns: Math.max(0, pl.statistics.totalTurns - 1) };
            turnStatsAdjusted.add(pl.id);
          }
          updatedPlayers[idx] = pl;
        }
        break;
      }
      case 'OVERTAKE': {
        const { overtakerId, bonus, penalties } = ev.payload;
        const overtakerIdx = updatedPlayers.findIndex(p => p.id === overtakerId);
        if (overtakerIdx !== -1) {
          const pl = { ...updatedPlayers[overtakerIdx] };
          pl.score -= bonus;
          if (!turnStatsAdjusted.has(pl.id)) {
            pl.statistics = {
              ...pl.statistics,
              totalTurns: Math.max(0, pl.statistics.totalTurns - 1),
              totalOvertakes: Math.max(0, (pl.statistics.totalOvertakes || 0) - penalties.length),
            };
            turnStatsAdjusted.add(pl.id);
          }
          updatedPlayers[overtakerIdx] = pl;
        }
        penalties.forEach((pp) => {
          const pi = updatedPlayers.findIndex(p => p.id === pp.playerId);
          if (pi !== -1) {
            const pl = { ...updatedPlayers[pi] };
            pl.score -= pp.delta;
            updatedPlayers[pi] = pl;
          }
        });
        break;
      }
      case 'ZONE_TRIGGERED': {
        // Odkrycie strefy zostaje – cofamy wyłącznie punkty.
        const { playerId, zoneDelta, overtakeBonus, penalties } = ev.payload;
        const total = (zoneDelta || 0) + (overtakeBonus || 0);
        const idx = updatedPlayers.findIndex(p => p.id === playerId);
        if (idx !== -1) {
          const pl = { ...updatedPlayers[idx] };
          pl.score -= total;
          if (!turnStatsAdjusted.has(pl.id)) {
            pl.statistics = {
              ...pl.statistics,
              totalTurns: Math.max(0, pl.statistics.totalTurns - 1),
              totalOvertakes: Math.max(0, (pl.statistics.totalOvertakes || 0) - penalties.length),
            };
            turnStatsAdjusted.add(pl.id);
          }
          updatedPlayers[idx] = pl;
        }
        penalties.forEach((pp) => {
          const pi = updatedPlayers.findIndex(p => p.id === pp.playerId);
          if (pi !== -1) {
            const pl = { ...updatedPlayers[pi] };
            pl.score -= pp.delta;
            updatedPlayers[pi] = pl;
          }
        });
        break;
      }
    }
  }

  updatedPlayers = updatePlayerPositions(updatedPlayers);
  const remainingEvents = eventList.filter(e => e.turn !== turn);
  const remainingHistory = state.history.filter(h => h.turn !== turn);
  const remainingTurns = remainingEvents.filter(e => e.type === 'TURN_END').map(e => e.turn);
  const newTurnNumber = remainingTurns.length > 0 ? Math.max(...remainingTurns) : 1;
  const replayPlayerId = turnEnd.payload.playerId;
  const currentPlayerIndex = updatedPlayers.findIndex(p => p.id === replayPlayerId);
  const newTurnCycle: NonNullable<GameState['turnCycle']> = {
    activePlayerId: replayPlayerId,
    rollCount: 0,
    dice: [],
    kept: makeEmptyDiceMask(),
    status: 'IDLE',
  };

  return {
    ...state,
    players: updatedPlayers,
    history: remainingHistory,
    events: remainingEvents,
    turnNumber: newTurnNumber,
    currentPlayerIndex: currentPlayerIndex === -1 ? 0 : currentPlayerIndex,
    turnCycle: newTurnCycle,
    isGameOver: false,
    winner: undefined,
  };
}
